import { Edit } from '@mui/icons-material'
import { Button, Card, CardContent, CircularProgress, Grid, Typography, Divider } from '@mui/material'
import { InboundTripDto, InboundTripImportBranchDto } from 'dtos'
import React, { useState, useEffect } from 'react'
import * as controllers from 'controllers'
import ImportBranchAddEditDialog from './ImportBranchAddEditDialog'
import { LabelField } from 'components'
import { enqueueSnackbar } from 'notistack'
import { useFetcher } from 'react-router-dom'
import { useLoadingState } from 'hooks'

interface ImportBranchListCardProps {
  inboundTrip: InboundTripDto
}

export default function ImportBranchListCard({ inboundTrip }: ImportBranchListCardProps) {
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingInboundTripImportBranch: false,
    isUpdatingInboundTripImportBranch: false
  })
  const [importBranchAddEditDialogOpen, setImportBranchAddEditDialogOpen] = React.useState<boolean>(false)
  const [inboundTripImportBranch, setInboundTripImportBranch] = useState<InboundTripImportBranchDto>(
    new InboundTripImportBranchDto()
  )

  useEffect(() => {
    if (inboundTrip.inboundTripGuid) {
      setLoadingState('isGettingInboundTripImportBranch', true)
      controllers
        .getInboundTripImportBranch(inboundTrip.inboundTripGuid)
        .then(response => {
          setInboundTripImportBranch(response.value)
        })
        .finally(() => {
          setLoadingState('isGettingInboundTripImportBranch', false)
        })
    }
  }, [inboundTrip.inboundTripGuid])

  return (
    <>
      <ImportBranchAddEditDialog
        initialValues={inboundTripImportBranch}
        inboundTrip={inboundTrip}
        isLoading={loadingState.isUpdatingInboundTripImportBranch}
        onClose={() => {
          setImportBranchAddEditDialogOpen(false)
        }}
        onSave={values => {
          setLoadingState('isUpdatingInboundTripImportBranch', true)
          controllers
            .updateInboundTripImportBranch(inboundTrip.inboundTripGuid!, values)
            .then(response => {
              setInboundTripImportBranch(response.value)
              setImportBranchAddEditDialogOpen(false)
              enqueueSnackbar('Import Branch Saved Successfully!', { variant: 'success' })
            })
            .finally(() => {
              setLoadingState('isUpdatingInboundTripImportBranch', false)
            })
        }}
        open={importBranchAddEditDialogOpen}
      />

      <Card>
        <CardContent>
          <Grid container spacing={1} alignItems='flex-start'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={6}>
                <Typography fontWeight='bold' variant='h5'>
                  Import Branch
                </Typography>
              </Grid>

              <Grid item xs={12} sm='auto'>
                <Button
                  endIcon={<Edit />}
                  onClick={() => {
                    setImportBranchAddEditDialogOpen(true)
                  }}
                  variant='text'
                  disabled={inboundTrip.isLocked}
                >
                  EDIT
                </Button>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {loadingState.isGettingInboundTripImportBranch && (
              <Grid item container alignItems='center' justifyContent='center' sx={{ mt: 1 }}>
                <Grid item>
                  <CircularProgress color='primary' size={50} />
                </Grid>
              </Grid>
            )}

            {!loadingState.isGettingInboundTripImportBranch && (
              <>
                <Grid item xs={12}>
                  <LabelField label='Import Branch' value={inboundTripImportBranch.importBranch?.name} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Port of Lading' value={inboundTripImportBranch.portOfLading?.cityProvince} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Port of Entry' value={inboundTripImportBranch.portOfEntry?.cityState} />
                </Grid>

                <Grid item xs={12}>
                  <LabelField label='Port Code' value={inboundTripImportBranch.portOfEntry?.portCode} />
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
